import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import api from '../api/client';
import { Wallet, WalletTransaction } from '../types/stores';
import { DeliveryZoneResponse, PaginatedResponse } from '../types';
import { useCompanyWallet } from './CompanyWalletContext';
import { useAnalytics } from './AnalyticsContext';

/* ============================= */
/* TYPES الأساسية للمتجر */
/* ============================= */

export interface DeliveryZone {
  id: string;
  city: string;
  governorate?: string;
  fee: number;
  estimatedDays: number;
  isActive: boolean;
}

interface OperationalHours {
  day: string;
  isOpen: boolean;
  openTime: string;
  closeTime: string;
  is24Hours?: boolean;
}

export interface StoreSettings {
  id: string;
  name: string;
  minimumOrder?: number;
  deliveryEnabled: boolean;
  pickupEnabled: boolean;
  paymentMethods: string[];
  contactNumber: string;
  address: string;
  pendingOrders: string;
}

/* ============================= */
/* CONTEXT TYPE */
/* ============================= */

interface StoreSettingsContextType {
  storeSettings: StoreSettings | null;
  deliveryZones: DeliveryZone[] | null;
  operationalHours: OperationalHours[];

  wallet: Wallet | null;
  walletTransactions: WalletTransaction[];

  isLoading: boolean;
  hasSettings: boolean;
  error: string | null;

  updateSettings: (settings: Partial<StoreSettings>) => Promise<void>;
  isStoreOpen: () => boolean;

  fetchWallet: () => Promise<void>;
  fetchWalletTransactions: () => Promise<void>;
  withdrawFromWallet: (amount: number) => Promise<void>;
  fetchStoreSettings: () => Promise<void>;
  resetSettings: () => void;
}

/* ============================= */
/* CREATE CONTEXT */
/* ============================= */

const StoreSettingsContext = createContext<StoreSettingsContextType | undefined>(undefined);

/* ============================= */
/* PROVIDER */
/* ============================= */

export const StoreSettingsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [storeSettings, setStoreSettings] = useState<StoreSettings | null>(null);
  const [deliveryZones, setDeliveryZones] = useState<DeliveryZone[] | null>(null);
  const [operationalHours] = useState<OperationalHours[]>([]); // always empty, store always open
  const { trackEvent } = useAnalytics ();

  const { fetchTransactions } = useCompanyWallet();

  const [wallet, setWallet] = useState<Wallet | null>(null);
  const [walletTransactions, setWalletTransactions] = useState<WalletTransaction[]>([]);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /* ============================= */
  /* HELPER FUNCTIONS */
  /* ============================= */
  
  const hasSettings = !!storeSettings;

  const resetSettings = () => {
    setStoreSettings(null);
    setDeliveryZones(null);
    setWallet(null);
    setWalletTransactions([]);
    setError(null);
  };

  /* ============================= */
  /* FETCH STORE SETTINGS */
  /* ============================= */
  const fetchStoreSettings = useCallback(async () => {
    try {
      const user = window.localStorage.getItem('user');
      if (!user) {
        setError('يجب تسجيل الدخول أولاً');
        setIsLoading(false);
        return;
      }

      const storeResponse = await api.get<StoreSettings>('/store/settings/');
      setStoreSettings(storeResponse.data);
      setError(null);
    } catch (error: any) {
      console.error('Failed to fetch store settings', error);
      
      // تحديد نوع الخطأ
      if (error.response?.status === 404) {
        setError('لم يتم العثور على إعدادات المتجر. يرجى إعداد متجرك أولاً.');
      } else if (error.response?.status === 401 || error.response?.status === 403) {
        setError('ليس لديك صلاحية للوصول إلى إعدادات المتجر');
      } else if (error.response?.status === 400) {
        setError('بيانات المتجر غير صالحة');
      } else {
        setError('حدث خطأ في تحميل إعدادات المتجر. يرجى المحاولة مرة أخرى.');
      }
      
      setStoreSettings(null);
    }
  }, []);

  /* ============================= */
  /* FETCH DELIVERY ZONES */
  /* ============================= */
  const fetchDeliveryZones = useCallback(async () => {
    try {
      const zonesResponse = await api.get<DeliveryZoneResponse>('/store/delivery-zones/');
      setDeliveryZones(zonesResponse.data.results);
    } catch (error) {
      console.error('Failed to fetch delivery zones', error);
      setDeliveryZones([]);
    }
  }, []);

  /* ============================= */
  /* FETCH WALLET */
  /* ============================= */
  const fetchWallet = useCallback(async () => {
    try {
      const { data } = await api.get<PaginatedResponse<Wallet>>('/wallets/');
      setWallet(data.results.length > 0 ? data.results[0] : null);
    } catch (error) {
      console.error('Failed to fetch wallet', error);
      setWallet(null);
    }
  }, []);

  /* ============================= */
  /* FETCH WALLET TRANSACTIONS */
  /* ============================= */
  const fetchWalletTransactions = useCallback(async () => {
    try {
      const { data } = await api.get<PaginatedResponse<WalletTransaction>>('/transactions/');
      setWalletTransactions(data.results);
    } catch (error) {
      console.error('Failed to fetch wallet transactions', error);
      setWalletTransactions([]);
    }
  }, []);

  /* ============================= */
  /* FETCH INITIAL DATA */
  /* ============================= */
  const fetchInitialData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const user = window.localStorage.getItem('user');
      if (!user) {
        setError('يجب تسجيل الدخول أولاً');
        setIsLoading(false);
        return;
      }

      // جلب جميع البيانات بشكل متوازي
      await Promise.allSettled([
        fetchStoreSettings(),
        fetchDeliveryZones(),
        fetchWallet(),
        fetchWalletTransactions()
      ]);

    } catch (error: any) {
      console.error('Failed to fetch initial store data', error);
      setError('حدث خطأ في تحميل بيانات المتجر. يرجى تحديث الصفحة.');
    } finally {
      setIsLoading(false);
    }
  }, [fetchStoreSettings, fetchDeliveryZones, fetchWallet, fetchWalletTransactions]);

  /* ============================= */
  /* INITIAL FETCH */
  /* ============================= */
  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  /* ============================= */
  /* UPDATE SETTINGS */
  /* ============================= */
  const updateSettings = async (settings: Partial<StoreSettings>) => {
    if (!storeSettings) {
      throw new Error('لا توجد إعدادات متجر لتحديثها');
    }
    
    try {
      setStoreSettings(prev => prev ? { ...prev, ...settings } : null);
      await api.patch('/store/settings/', settings);
    } catch (error: any) {
      console.error('Failed to update settings', error);
      throw error;
    }
  };

  /* ============================= */
  /* CREATE INITIAL SETTINGS */
  /* ============================= */
  const createInitialSettings = async (settings: Omit<StoreSettings, 'id'>) => {
    try {
      const response = await api.post<StoreSettings>('/store/settings/', settings);
      setStoreSettings(response.data);
      setError(null);
      return response.data;
    } catch (error: any) {
      console.error('Failed to create store settings', error);
      throw error;
    }
  };

  /* ============================= */
  /* STORE OPEN CHECK */
  /* ============================= */
  const isStoreOpen = () => true; // always open

  /* ============================= */
  /* WITHDRAW MONEY */
  /* ============================= */
  const withdrawFromWallet = async (amount: number) => {
    if (!wallet) throw new Error('لم يتم العثور على المحفظة');
    
    try {
      await api.post('/wallets/withdraw/', { amount, sender_wallet: wallet.id });
      await fetchWallet();
      await fetchWalletTransactions();
    } catch (error: any) {
      console.error('Failed to withdraw from wallet', error);
      throw error;
    }
  };
  // 🔹 Listener مركزي على تغيير إعدادات المتجر
  useEffect(() => {
    if (storeSettings) {
      trackEvent('store_settings_loaded', {
        store_id: storeSettings.id,
        delivery_enabled: storeSettings.deliveryEnabled,
        payment_methods: storeSettings.paymentMethods,
      });
    }
  }, [storeSettings, trackEvent]);

  // 🔹 Listener مركزي على تحديث المحفظة
  useEffect(() => {
    if (wallet) {
      trackEvent('wallet_loaded', {
        wallet_id: wallet.id,
        balance: wallet.balance,
      });
    }
  }, [wallet, trackEvent]);

  // 🔹 Listener مركزي على تغيير المعاملات
  useEffect(() => {
    if (walletTransactions.length > 0) {
      trackEvent('wallet_transactions_loaded', {
        transactions_count: walletTransactions.length,
        wallet_id: wallet?.id,
      });
    }
  }, [walletTransactions, wallet, trackEvent]);

  /* ============================= */
  /* PROVIDER VALUE */
  /* ============================= */
  const contextValue: StoreSettingsContextType = {
    storeSettings,
    deliveryZones,
    operationalHours,
    wallet,
    walletTransactions,
    isLoading,
    hasSettings,
    error,
    updateSettings,
    isStoreOpen,
    fetchWallet,
    fetchWalletTransactions,
    withdrawFromWallet,
    fetchStoreSettings,
    resetSettings
  };

  return (
    <StoreSettingsContext.Provider value={contextValue}>
      {children}
    </StoreSettingsContext.Provider>
  );
};

/* ============================= */
/* CUSTOM HOOK */
/* ============================= */
export const useStoreSettings = () => {
  const context = useContext(StoreSettingsContext);
  if (!context) throw new Error('useStoreSettings must be used within StoreSettingsProvider');
  return context;
};